import { useEffect } from 'react';
import { useBlocker, useNavigate, type Location } from 'react-router';

import { CabinetButton, ModalFrame, SecondaryButton } from './Kit';

type Props = {
  when: boolean;
  audience: 'student' | 'tutor';
  localOpen?: boolean;
  onLocalStay?: () => void;
  onLocalDiscard?: () => void;
  onSave: () => Promise<boolean>;
};

export function UnsavedLeaveGuard({
  when,
  audience,
  localOpen = false,
  onLocalStay,
  onLocalDiscard,
  onSave,
}: Props) {
  const blocker = useBlocker(when);
  const navigate = useNavigate();
  const routeBlocked = blocker.state === 'blocked';
  const open = routeBlocked || localOpen;

  useEffect(() => {
    if (!when) return;
    const onLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [when]);

  if (!open) return null;

  const stay = () => {
    if (routeBlocked) blocker.reset();
    onLocalStay?.();
  };

  const leaveAfterSave = (pending: Location | null) => {
    if (pending && blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }
    onLocalDiscard?.();
    onLocalStay?.();
    if (pending) navigate(pending);
  };

  const text =
    audience === 'student'
      ? 'Сохрани правки и отправь на проверку, сбрось черновик или останься в анкете.'
      : 'Сохраните правки и отправьте на проверку, сбросьте черновик или останьтесь в анкете.';

  return (
    <ModalFrame title="Есть несохраненные правки" text={text} onClose={stay}>
      <CabinetButton
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const pending = routeBlocked ? blocker.location : null;
          void (async () => {
            let ok = false;
            try {
              ok = await onSave();
            } catch (error) {
              console.error(error);
              ok = false;
            }
            if (!ok) {
              stay();
              return;
            }
            leaveAfterSave(pending);
          })();
        }}
      >
        Сохранить
      </CabinetButton>
      <SecondaryButton
        type="button"
        onClick={() => {
          if (routeBlocked) blocker.proceed();
          else onLocalDiscard?.();
          onLocalStay?.();
        }}
      >
        Сбросить
      </SecondaryButton>
      <SecondaryButton type="button" onClick={stay}>
        Остаться
      </SecondaryButton>
    </ModalFrame>
  );
}
